import React, { useEffect, FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { useQueryClient } from 'react-query';
import { Content, CustomType } from 'mdello-client-ts/dist/src/types/mdello/cms.type';
import { makeStyles } from '@material-ui/core';
import { Tag } from 'antd';
import { useGetContentsQuery } from 'src/reactQuery/Content/useGetContents';
import Loader from 'src/component/Loader/Loader';

import { useGetContentDraftMap } from 'src/reactQuery/Content/useContentDraftMap/useGetContentDraftMap';
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum';
import CButton from 'src/component/Button';
import { updateContent } from 'src/services/napi';
import ContentDetailBodyRecursive from './ContentDetaiBodyRecursive';
import formatDate from '../../../helpers/formatDate';
import Redirect from '../../../asset/svg/Redirect.svg';

const useStyles = makeStyles({
  contentModel: {
    display: 'flex',
    flexDirection: 'column'
  },
  contentModelRow: {
    '&:hover': {
      backgroundColor: '#fafafa',
      cursor: 'pointer'
    },
    '&:last-child': {
      borderBottom: 'none'
    },
    alignItems: 'center',
    borderBottom: '1px solid #f1f4fe',
    display: 'grid',
    gridTemplateColumns: '50% 20% 14% 8% 8%',
    gridTemplateRows: 'repeat(2, minmax(70px, auto))',
    height: '70px',
    justifyItems: 'end',
    padding: '0px 40px 0px 35px'
  },
  date: {
    fontWeight: 400,
    textAlign: 'center',
    width: '100%'
  },
  expandedContent: {
    backgroundColor: 'red',
    display: 'block',
    height: '100%',
    width: '100%'
  },
  expandedRow: {
    '&:hover': {
      backgroundColor: '#fff'
    },
    height: 'fit-content',
    padding: '0px 40px 20px 35px'
  },
  headers: {
    '& > p': {
      color: '#9ba0ad'
    },
    alignItems: 'center',
    borderBottom: '1px solid #f1f4fe',
    display: 'grid',
    gridTemplateColumns: '50% 20% 14% 8% 8%',
    gridTemplateRows: 'repeat(2, minmax(70px, auto))',
    height: '70px',
    justifyItems: 'center',
    padding: '0px 40px 0px 35px'
  },
  hiddenContent: {
    display: 'none'
  },
  name: {
    fontWeight: 700,
    textAlign: 'start',
    width: '100%'
  },
  nameColumn: {
    textAlign: 'start',
    width: '100%'
  },
  redirect: {
    alignItems: 'center',
    height: '36px',
    padding: '8px 0px 4px 0px',
    width: '66px'
  },
  tag: {
    alignItems: 'center',
    border: 'none',
    borderRadius: '12px',
    display: 'flex',
    height: '40px',
    justifyContent: 'center',
    margin: '0px',
    padding: '0px',
    width: '100%'
  }
});

interface ContentDetailBodyRecursiveProps {
  record: MappedContents;
}

const expandedRowRender = ({ record }: ContentDetailBodyRecursiveProps) => {
  return <ContentDetailBodyRecursive contentId={record._id} />;
};

interface MappedContents {
  _id: string;
  contentType: string;
  created: string;
  // eslint-disable-next-line camelcase
  custom_type: CustomType;
  key: number;
  name: string;
  status: string;
  updated: string;
}

interface ContentModelProps {
  contentModelId: string;
  setEntitiesCount: (value: number) => void;
}

const ContentModel: FC<ContentModelProps> = ({ contentModelId, setEntitiesCount }) => {
  const history = useHistory();
  const urlLocation = useLocation();
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [openedContents, setOpenedContents] = useState<number[]>([]);

  const { data: contentDraftMap } = useGetContentDraftMap();

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  const { data: contentData, isLoading: isLoadingContent } = useGetContentsQuery({
    customTypeId: contentModelId,
    orderBy: params.orderBy?.toString(),
    orderDirection: params.orderDirection?.toString(),
    page: params.page?.toString() || '1',
    pageSize: params.pageSize?.toString() || '1000',
    searchItem: params.search?.toString()
  });

  useEffect(() => {
    if (!contentData || !contentDraftMap) {
      return;
    }

    setEntitiesCount(contentData.paging.total);
    const combinedArray = contentData.content.concat(contentDraftMap);
    const uniqueArray = combinedArray.filter(
      (obj, index, self) => index === self.findIndex((t) => t._id === obj._id)
    );

    queryClient.setQueryData(QueryKey.CONTENT_DRAFT_MAP, uniqueArray);
  }, [contentData]);

  const mapToTable = (content: Content<any>[] | undefined): MappedContents[] => {
    const data: MappedContents[] = [];

    if (!content || content.length === 0) {
      return data;
    }

    content.forEach(
      // @ts-ignore
      // eslint-disable-next-line camelcase
      ({ _id, name, customTypeName, created, updated, status, custom_type }, index) => {
        const createdDate = formatDate(created);
        const updatedDate = formatDate(updated);

        data.push({
          _id,
          contentType: customTypeName,
          created: createdDate,
          custom_type,
          key: index,
          name,
          status,
          updated: updatedDate
        });
      }
    );
    return data;
  };

  const openDetail = (item: MappedContents) => {
    history.push(`/content/${item._id}`);
  };

  const onSaveClick = async (index: number) => {
    const selectedContentDraft = contentDraftMap && contentDraftMap[index];
    if (selectedContentDraft) {
      await updateContent(
        selectedContentDraft,
        selectedContentDraft._id,
        selectedContentDraft.project
      );
    }
    onDivClick(index);
  };

  const onDivClick = (index: number) => {
    if (openedContents.includes(index)) {
      const newContents = [...openedContents];
      newContents.splice(openedContents.indexOf(index), 1);
      setOpenedContents(newContents);
    } else {
      const newContents = [...openedContents];
      newContents.push(index);
      setOpenedContents(newContents);
    }
  };

  if (isLoadingContent) {
    return <Loader />;
  }

  const data = mapToTable(contentData?.content);

  return (
    <div className={classes.contentModel}>
      <div className={classes.headers}>
        <p className={classes.nameColumn}>Name</p>
        <p>Published</p>
        <p>Status</p>
        <p />
        <p />
      </div>
      {data.map((content, index) => {
        return (
          <div
            key={content._id}
            className={`${classes.contentModelRow} ${
              openedContents.includes(index) ? classes.expandedRow : ''
            }`}
            onClick={() => onDivClick(index)}
          >
            <div className={classes.name}>{content.name}</div>
            <div className={classes.date}>{formatDate(content.created)}</div>
            <Tag
              className={classes.tag}
              style={
                content.status === 'published'
                  ? {
                      backgroundColor: 'rgba(65, 204, 156, 0.15)',
                      color: '#41CC9C'
                    }
                  : {
                      backgroundColor: 'rgba(244, 211, 37, 0.15)',
                      color: '#F4CD00'
                    }
              }
            >
              {content.status}
            </Tag>
            <img
              alt="redirect"
              className={classes.redirect}
              onClick={() => openDetail(content)}
              src={Redirect}
            />
            <CButton
              onClick={() => onSaveClick(index)}
              type="primary"
              xstyle={{
                marginLeft: '0px',
                maxWidth: 'fit-content',
                minWidth: '66px'
              }}
            >
              {openedContents.includes(index) ? 'Save' : 'Edit'}
            </CButton>
            <div className={classes.hiddenContent} />
            {openedContents.length && openedContents?.includes(index)
              ? expandedRowRender({ record: content })
              : null}
          </div>
        );
      })}
    </div>
  );
};

export default ContentModel;
